import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { CommodityType } from '@bechtel/types';
import { NetworkStatus } from '@apollo/client';
import { FieldArray, FieldProps } from 'formik';
import { filter, findIndex, get, some } from 'lodash';
import React, { Fragment } from 'react';
import { ErrorRetry } from '~/components/atoms/error-retry';
import { ListSelectionStyled, SubListSelectionStyled } from '~/components/ions/drawer';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { useProjectCommodityTypesQuery } from '~/hooks/use-project-commodity-types';
import { t, Trans } from '@lingui/macro';

interface CommodityTypeListProps extends FieldProps {
  excludeCommodityTypes?: string[];
}

export const CommodityTypeList: React.FC<CommodityTypeListProps> = ({ form, excludeCommodityTypes = [] }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useProjectCommodityTypesQuery(user?.project?.id);

  const commodityTypeList = filter(
    get(data, 'projectConfigById.projectCommodityTypes', []) as CommodityType[],
    (commodityType) => !excludeCommodityTypes.includes(commodityType.name) // Exclude specified commodity types
  );
  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Commodity Type List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.filterByCommodity?.length !== commodityTypeList.length) {
                form.setFieldValue('filterByCommodity', commodityTypeList);
              } else {
                form.setFieldValue('filterByCommodity', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.filterByCommodity?.length === commodityTypeList.length}
                  indeterminate={
                    form.values.filterByCommodity?.length > 0 &&
                    form.values.filterByCommodity?.length < commodityTypeList.length
                  }
                  readOnly
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans> Commodity Type</Trans>
              </Text>
            </Box>
            {form.values.filterByCommodity && form.values.filterByCommodity.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.filterByCommodity.length} {t`Selected`}
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="filterByCommodity">
            {(arrayHelpers) => {
              return (
                <>
                  {commodityTypeList &&
                    commodityTypeList.map((commodityType: CommodityType) => {
                      return (
                        <ListSelectionStyled
                          key={commodityType.id}
                          data-testid={`planning-wp-filter-by-commodity-types-${commodityType.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.filterByCommodity, (item: CommodityType) => {
                              return item.id === commodityType.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(commodityType);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.filterByCommodity, (item) => {
                                return item.id === commodityType.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {commodityType.name}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
